import { type ProjectRolesToolsOrganizations_portfolioProject$key } from '#app/__generated__/relay/ProjectRolesToolsOrganizations_portfolioProject.graphql.js';
import { BlogCategoriesList } from '#app/components/BlogCategoriesList/BlogCategoriesList.js';
import { OrganizationsList } from '#app/components/OrganizationsList/OrganizationsList.js';
import { Text } from '#app/components/Primitives/Text.js';
import { RolesList } from '#app/components/RolesList/RolesList.js';
import { ToolsList } from '#app/components/ToolsList/ToolsList.js';
import { getContraImageUrl } from '#app/utilities/getContraImageUrl.js';
import { CalendarIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-flow: row wrap;
  margin-top: -10px;

  & > * {
    margin-top: 10px;
    margin-right: 8px;
    // todo: remove margin left reset once we remove list styles (& + & {...}) from the Tag component
    margin-left: 0 !important;
  }
`;

const PublishedAtTag = styled(Text).attrs({
  textStyle: 'bodySmall',
})`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    border-radius: 6px;
    margin-left: 16px;
    color: ${theme.colorsV2.black};

    svg {
      margin-right: 8px;
    }
  `}
`;

type ProjectRolesToolsCompaniesProps = {
  readonly projectRef: ProjectRolesToolsOrganizations_portfolioProject$key;
};

export const ProjectRolesToolsOrganizations = ({
  projectRef,
}: ProjectRolesToolsCompaniesProps) => {
  const project =
    useFragment<ProjectRolesToolsOrganizations_portfolioProject$key>(
      graphql`
        fragment ProjectRolesToolsOrganizations_portfolioProject on PortfolioProject {
          author {
            id
          }
          roles {
            edges {
              node {
                id
                name
              }
            }
          }

          tools {
            edges {
              node {
                id
                name
                logoImage {
                  uid
                }
              }
            }
          }

          organizations {
            edges {
              node {
                name
                logoImage {
                  uid
                  url
                }
              }
            }
          }
          blogCategories {
            edges {
              node {
                id
                name
              }
            }
          }
          publishedAt
        }
      `,
      projectRef,
    );

  const roles = project.roles.edges.map(({ node }) => ({
    id: node.id,
    name: node.name,
  }));
  const tools = project.tools.edges.map(({ node }) => node);
  const categories = project.blogCategories?.edges.map(({ node }) => node);

  const organizations = project.organizations.edges.map(({ node }) => ({
    icon: node.logoImage?.url
      ? getContraImageUrl({
          uid: node.logoImage?.uid,
        })
      : undefined,
    name: node.name,
  }));
  const isBlog = Boolean(project.author);

  return (
    <Container>
      {roles.length ? (
        <RolesList
          roles={roles}
          variant="whiteBorder"
        />
      ) : null}
      {tools.length ? (
        <ToolsList
          tools={tools}
          variant="whiteBorder"
        />
      ) : null}
      {categories?.length ? (
        <BlogCategoriesList categories={categories} />
      ) : null}
      {organizations.length ? (
        <OrganizationsList organizations={organizations} />
      ) : null}

      {project.publishedAt && isBlog ? (
        <PublishedAtTag>
          <CalendarIcon size={16} />
          {new Intl.DateTimeFormat('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }).format(new Date(project.publishedAt))}
        </PublishedAtTag>
      ) : null}
    </Container>
  );
};
